import { useState } from 'react';
import { getAutoAdvisorAnalyses, removeAutoAdvisorAnalysis } from '../../utils/storage';

const INTENTS = [
  { key: 'curious', emoji: '\uD83D\uDD0D', label: 'Just Curious', desc: 'Learning about this model' },
  { key: 'passive', emoji: '\uD83D\uDC40', label: 'Passively Looking', desc: 'Monitoring the market' },
  { key: 'active', emoji: '\uD83C\uDFAF', label: 'Actively Shopping', desc: 'Comparing specific examples' },
  { key: 'ready', emoji: '\u2705', label: 'Ready to Buy', desc: 'About to make an offer' },
];

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return '';
  }
}

export default function StepIntent({ data, update, onNext, onBack, advisorMode, onSelectMode, onViewSaved, onRefreshSaved }) {
  const [showAllReports, setShowAllReports] = useState(false);
  const [reports, setReports] = useState(() => getAutoAdvisorAnalyses());

  // `onViewSaved` is overloaded: on the mode-selector screen it opens a
  // single saved report; on the purchase intent screen the same prop is
  // used to jump back to the saved-reports list. We branch on which
  // screen we're rendering to decide the meaning.
  const hasSavedReports = reports.length > 0;

  function handleDelete(savedAt) {
    const updated = removeAutoAdvisorAnalysis(savedAt);
    setReports(updated);
  }

  // Mode selector — shown when advisorMode is not yet set
  if (!advisorMode) {
    const visibleReports = showAllReports ? reports.slice().reverse() : reports.slice().reverse().slice(0, 5);

    return (
      <>
        <div style={{ marginBottom: 24 }}>
          <h1
            style={{
              fontSize: '1.5rem',
              fontWeight: 800,
              color: '#D3474A',
              marginBottom: 10,
              letterSpacing: '-0.01em',
            }}
          >
            Auto Advisor
          </h1>
          <p
            style={{
              fontSize: '0.9rem',
              lineHeight: 1.55,
              color: 'var(--text-secondary)',
              margin: 0,
            }}
          >
            Smart insights to help you buy smarter and avoid overpaying
            for repairs. Choose what you need help with.
          </p>
        </div>

        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 14, paddingTop: 8 }}>
          {/* Pre-Purchase Research card */}
          <button
            className="card"
            onClick={() => onSelectMode('purchase')}
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 16,
              textAlign: 'left',
              cursor: 'pointer',
              padding: '20px',
              transition: 'all 0.2s',
            }}
          >
            <span style={{ fontSize: '2rem', flexShrink: 0, marginTop: 2 }}>🔍</span>
            <div>
              <div style={{ fontWeight: 700, fontSize: '1.0625rem', color: 'var(--text-primary)', marginBottom: 6 }}>
                Pre-Purchase Research
              </div>
              <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                Get market analysis, comparable sales, common issues, and negotiation tips before you buy a vehicle.
              </div>
            </div>
          </button>

          {/* Verify Repair Quote card */}
          <button
            className="card"
            onClick={() => onSelectMode('repair')}
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 16,
              textAlign: 'left',
              cursor: 'pointer',
              padding: '20px',
              transition: 'all 0.2s',
            }}
          >
            <span style={{ fontSize: '2rem', flexShrink: 0, marginTop: 2 }}>🔧</span>
            <div>
              <div style={{ fontWeight: 700, fontSize: '1.0625rem', color: 'var(--text-primary)', marginBottom: 6 }}>
                Verify Repair Quote
              </div>
              <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                Upload or enter a repair estimate to check if the pricing is fair for your vehicle and location.
              </div>
            </div>
          </button>

          {/* Saved Reports */}
          {reports.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <h3 style={{ fontSize: '0.9375rem', fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>
                  Saved Reports
                </h3>
                <span
                  style={{
                    fontSize: '0.6875rem',
                    fontWeight: 600,
                    background: 'var(--accent)',
                    color: 'white',
                    borderRadius: 100,
                    padding: '2px 8px',
                    minWidth: 20,
                    textAlign: 'center',
                  }}
                >
                  {reports.length}
                </span>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {visibleReports.map((report) => {
                  const label = report.results?.vehicleLabel || report.vehicleLabel || 'Unknown Vehicle';
                  const isRepair = !!report.repairDescription || !!report.repairQuotePhoto;

                  return (
                    <div
                      key={report.savedAt}
                      className="card"
                      style={{ padding: '14px 16px' }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--text-primary)', marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {label}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ fontSize: '0.6875rem', color: 'var(--text-muted)' }}>
                              {formatDate(report.savedAt)}
                            </span>
                            <span
                              style={{
                                fontSize: '0.625rem',
                                fontWeight: 600,
                                padding: '1px 6px',
                                borderRadius: 4,
                                background: isRepair ? 'rgba(245, 158, 11, 0.12)' : 'rgba(59, 130, 246, 0.12)',
                                color: isRepair ? 'var(--warning)' : '#3b82f6',
                              }}
                            >
                              {isRepair ? 'Repair' : 'Purchase'}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div style={{ display: 'flex', gap: 8 }}>
                        <button
                          onClick={() => onViewSaved && onViewSaved(report)}
                          style={{
                            flex: 1,
                            padding: '6px 0',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            borderRadius: 'var(--radius-sm)',
                            border: '1px solid var(--accent)',
                            background: 'transparent',
                            color: 'var(--accent)',
                            cursor: 'pointer',
                          }}
                        >
                          View
                        </button>
                        <button
                          onClick={() => onRefreshSaved && onRefreshSaved(report)}
                          style={{
                            flex: 1,
                            padding: '6px 0',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            borderRadius: 'var(--radius-sm)',
                            border: '1px solid var(--border)',
                            background: 'transparent',
                            color: 'var(--text-secondary)',
                            cursor: 'pointer',
                          }}
                        >
                          Refresh
                        </button>
                        <button
                          onClick={() => handleDelete(report.savedAt)}
                          style={{
                            padding: '6px 12px',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            borderRadius: 'var(--radius-sm)',
                            border: '1px solid var(--border)',
                            background: 'transparent',
                            color: 'var(--danger, #ef4444)',
                            cursor: 'pointer',
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>

              {reports.length > 5 && (
                <button
                  onClick={() => setShowAllReports(!showAllReports)}
                  style={{
                    width: '100%',
                    padding: '8px 0',
                    marginTop: 8,
                    fontSize: '0.8125rem',
                    fontWeight: 600,
                    color: 'var(--accent)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                  }}
                >
                  {showAllReports ? 'Show less' : `Show all ${reports.length} reports`}
                </button>
              )}
            </div>
          )}
        </div>

        <div className="nav-row">
          <button className="btn btn-secondary" onClick={onBack}>
            Back
          </button>
        </div>
      </>
    );
  }

  // Purchase mode — show intent selection
  function select(key) {
    update({ intent: key });
  }

  return (
    <>
      {/* Hero header */}
      <div style={{ marginBottom: 24 }}>
        <h1
          style={{
            fontSize: '1.5rem',
            fontWeight: 800,
            color: '#D3474A',
            marginBottom: 10,
            letterSpacing: '-0.01em',
          }}
        >
          Pre-Purchase Advisor
        </h1>
        <p
          style={{
            fontSize: '0.9rem',
            lineHeight: 1.55,
            color: 'var(--text-secondary)',
            margin: 0,
          }}
        >
          Get expert insights before you buy. We&apos;ll analyze market value,
          common issues, ownership costs, and help you negotiate with confidence.
          Whether you&apos;re browsing or ready to make an offer, we&apos;ll give you
          the research that matters.
        </p>
      </div>

      <div className="screen-header" style={{ marginBottom: 0 }}>
        <h2
          style={{
            fontSize: '1.0625rem',
            fontWeight: 700,
            color: 'var(--text-primary)',
            margin: 0,
          }}
        >
          What phase of the purchase process are you in?
        </h2>
      </div>

      <div style={{ flex: 1, paddingTop: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
        {INTENTS.map((item) => {
          const selected = data.intent === item.key;
          return (
            <button
              key={item.key}
              className="card"
              onClick={() => select(item.key)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 16,
                textAlign: 'left',
                cursor: 'pointer',
                borderColor: selected ? 'var(--accent)' : undefined,
                background: selected ? 'var(--accent-subtle)' : undefined,
                transition: 'all 0.2s',
              }}
            >
              <span style={{ fontSize: '1.75rem', flexShrink: 0 }}>{item.emoji}</span>
              <div>
                <div
                  style={{
                    fontWeight: 600,
                    fontSize: '0.9375rem',
                    color: selected ? 'var(--accent)' : 'var(--text-primary)',
                  }}
                >
                  {item.label}
                </div>
                <div
                  style={{
                    fontSize: '0.8125rem',
                    color: 'var(--text-secondary)',
                    marginTop: 2,
                  }}
                >
                  {item.desc}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* "View saved reports" sits above Back/Continue so users don't
          have to know to hit Back to find their history. Only rendered
          when there's actually something saved. */}
      {hasSavedReports && onViewSaved && (
        <button
          type="button"
          onClick={onViewSaved}
          style={{
            width: '100%',
            padding: '10px 0',
            marginTop: 12,
            fontSize: '0.8125rem',
            fontWeight: 600,
            color: 'var(--accent)',
            background: 'transparent',
            border: '1px solid var(--accent)',
            borderRadius: 'var(--radius-sm)',
            cursor: 'pointer',
          }}
        >
          View saved reports ({reports.length})
        </button>
      )}

      <div className="nav-row">
        <button className="btn btn-secondary" onClick={onBack}>
          Back
        </button>
        <button className="btn btn-primary" disabled={!data.intent} onClick={onNext}>
          Continue
        </button>
      </div>
    </>
  );
}
